import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TaskDetailsModal } from "@/components/tasks2/TaskDetailsModal";
import { NewTaskModal } from "./NewTaskModal";
import { Task2 } from "@/types/tasks";
import { useTasks } from "@/hooks/useTasks";
import { Plus, Circle, Clock, User, X, Minus, ChevronDown, RefreshCw, Activity } from "lucide-react";

const priorityConfig = {
  high: { color: "text-destructive", variant: "destructive" as const, label: "Alta" },
  medium: { color: "text-warning", variant: "secondary" as const, label: "Média" },
  low: { color: "text-success", variant: "outline" as const, label: "Baixa" },
};

const statusConfig = {
  not_started: { label: "Pendente", color: "bg-warning", icon: Clock },
  in_progress: { label: "Em Andamento", color: "bg-primary", icon: Circle },
  completed: { label: "Concluída", color: "bg-success", icon: Circle },
  waiting: { label: "Aguardando", color: "bg-blue-500", icon: Clock },
  cancelled: { label: "Cancelada", color: "bg-muted-foreground", icon: Circle },
};

interface TaskListProps {
  workId: string; // ID da obra atual
  onClose?: () => void;
  onMinimize?: () => void;
  isMinimized?: boolean;
}

export function TaskList({ workId, onClose, onMinimize, isMinimized }: TaskListProps) {
  const {
    tasks: allTasks,
    loading,
    updateTask,
    refetch
  } = useTasks();

  // Filter tasks by workId
  const tasks = allTasks.filter(task => task.workId === workId);

  // Calculate stats
  const taskStats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    notStarted: tasks.filter(t => t.status === 'not_started').length,
    overdue: tasks.filter(t => t.endDate && new Date() > t.endDate && t.status !== 'completed').length
  };

  const lastUpdated = new Date();

  const [selectedTask, setSelectedTask] = useState<Task2 | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);

  // Mock projects data for the modal
  const projects = [
    { id: "1", name: "Projeto Principal", color: "#0071CE" }
  ];

  const handleTaskClick = (task: Task2) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  };

  const handleTaskUpdate = (updatedTask: Task2) => {
    // Persiste as edições (antes só dava refetch e descartava as mudanças).
    updateTask(updatedTask.id, updatedTask);
  };

  const handleNewTaskCreated = (newTask: Task2) => {
    // Task is automatically added by the useTasks hook
    // Just refetch to ensure we have the latest data
    refetch();
  };

  // Helper function para format da data
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR');
  };

  const formatLastUpdated = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      <Card className="shadow-card transition-shadow">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <CardTitle className="text-lg font-semibold text-foreground">
                Tarefas ({taskStats.total})
              </CardTitle>
              {loading && (
                <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setIsNewTaskModalOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Tarefa
              </Button>
              {onMinimize && (
                <button
                  onClick={onMinimize}
                  className="p-1 rounded hover:bg-muted transition-colors"
                  title={isMinimized ? "Maximizar" : "Minimizar"}
                >
                  {isMinimized ? <ChevronDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1 rounded hover:bg-muted transition-colors"
                  title="Fechar"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        {!isMinimized && (
          <>
            
            <CardContent className="space-y-3 pt-0">
              {tasks.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhuma tarefa encontrada para esta obra</p>
                </div>
              ) : (
                tasks.map((task) => {
                  // Fallback: status/prioridade desconhecidos não podem quebrar a lista.
                  const priority = priorityConfig[task.priority as keyof typeof priorityConfig] ?? priorityConfig.medium;
                  const status = statusConfig[task.status as keyof typeof statusConfig] ?? statusConfig.not_started;
                  const StatusIcon = status.icon;
                  
                  return (
                    <div 
                      key={task.id}
                      className="p-3 rounded-lg border border-border bg-card hover:shadow-card transition-shadow cursor-pointer"
                      onClick={() => handleTaskClick(task)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${status.color}`} />
                          <h4 className="font-medium text-foreground">{task.title}</h4>
                        </div>
                        <Badge variant={priority.variant} className="text-xs">
                          {priority.label}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-3 pl-5">
                        {task.description}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs text-muted-foreground pl-5">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <User className="w-3 h-3" />
                            <span>{task.assignees[0]?.name || "Não atribuído"}</span>
                          </div>
                          {task.endDate && (
                            <div className="flex items-center space-x-1">
                              <Clock className="w-3 h-3" />
                              <span>{formatDate(task.endDate)}</span>
                            </div>
                          )}
                          <div className="text-xs">
                            {task.setor}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {status.label}
                        </Badge>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </>
        )}
      </Card>

        {/* Modal de Detalhes da Tarefa */}
        {selectedTask && (
          <TaskDetailsModal
            task={selectedTask}
            projects={projects}
            isOpen={isTaskModalOpen}
            onClose={() => {
              setIsTaskModalOpen(false);
              setSelectedTask(null);
            }}
            onUpdate={handleTaskUpdate}
            isFullscreen={false}
            onToggleFullscreen={() => {}}
          />
        )}

        {/* Modal de Nova Tarefa (condicional: desmonta ao fechar) */}
        {isNewTaskModalOpen && (
          <NewTaskModal
            open={isNewTaskModalOpen}
            onOpenChange={setIsNewTaskModalOpen}
            onTaskCreated={handleNewTaskCreated}
            defaultWorkId={workId}
          />
        )}
      </>
    );
  }